import { fabric } from "fabric";
import _ from "lodash";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Measure from "react-measure";
import { useDispatch, useSelector } from "react-redux";
import { setZoomableImageDimensions } from "redux/actions";
import { getRawApiImage } from "./RawImageComponent";
import { generateZoneGrids, generateZoomableImageBbox, generateZoomableImageBboxLabel, getColorFromPercentage, setBboxColor } from "./util/FabricUtils";
import OBCSpinner from "./util/OBC";
import { asyncLoadImageWithRange } from "./util/PlaylistUtils";
import { degreesToUserPreference, getTempUnit } from "./util/TemperatureUtils";

const EMPTY_FILTERS = {};
const EMPTY_OVERLAY_BOXES = [];
export const STROKE_WIDTH = 2;

const currentDashboardSelector = (state) => _.find(state.dashboards, (dashboard) => dashboard.access_token === state.access_token);
const observationDetectionsSelector = (state) => state.observationDetections;
const markerSelector = (state) => state.markers;
const tempUnitPreferenceSelector = (state) => state.userDetails.userConfig.temperature_units || "celsius";

const ZoomableImage = memo(
    ({
        imgSrc,
        strideImageData,
        zoom,
        updateZoom,
        overlayBoxes = EMPTY_OVERLAY_BOXES,
        overlays = [],
        filters = EMPTY_FILTERS,
        withSpinner = false,
        frame,
        videoKey,
        showOverlayContent,
        tag,
        disabled,
        isPictureInPicture,
        rounded,
    }) => {
        const [dimensions, setDimensions] = useState({});
        const [fabricCanvasRef, setFabricCanvasRef] = useState(null);
        const [fabricCanvas, setFabricCanvas] = useState(null);
        const [newHeight, setNewHeight] = useState(null);
        const [overlayWidth, setOverlayWidth] = useState(null);
        const fabricCanvasObjects = useRef([]);
        const canvasImageRef = useRef(null);

        const [ratioChangeForZoom, setRatioChangeForZoom] = useState(null);
        const [loadingImage, setLoadingImage] = useState(true);
        const [formattedImageSource, setFormattedImageSource] = useState();
        const [imageCacheTimestamp, setImageCacheTimestamp] = useState();

        const currentDashboard = useSelector(currentDashboardSelector);
        const observationDetections = useSelector(observationDetectionsSelector);
        const markers = useSelector(markerSelector);
        const tempUnitPreference = useSelector(tempUnitPreferenceSelector);

        const dispatch = useDispatch();

        useEffect(() => {
            if (fabricCanvas) {
                if (!showOverlayContent) {
                    fabricCanvas.remove(...fabricCanvasObjects.current);
                    return;
                }

                const selectedMarker = _.get(markers, "selectedMarker") || {};
                const detectionsToDisplay = observationDetections.filter(
                    (detection) =>
                        detection.video_key === videoKey && detection.frame === frame && detection.detection_class === _.get(selectedMarker, "class"),
                );

                fabricCanvas.remove(...fabricCanvasObjects.current);
                fabricCanvasObjects.current = [];

                const encroachmentBboxes = [];
                const zoneBboxes = [];
                const perspectivePoints = [];

                let leftMostPoint = null;
                let rightMostPoint = null;

                _.forEach(detectionsToDisplay, (detection) => {
                    if (_.get(detection, "detection_class") === "vegetation_encroachment") {
                        const encroachment_bbox = JSON.parse(detection.encroachment_bbox);
                        const zone_bbox = JSON.parse(detection.zone_bbox);
                        const cell_map_data = detection.cell_map_data;
                        const percentage = detection.encroachment_percentage;
                        const zoneSide = detection.zone;
                        encroachmentBboxes.push({ bbox: encroachment_bbox, percentage: percentage, zoneSide: zoneSide });
                        zoneBboxes.push({ bbox: zone_bbox, percentage: percentage, cell_map_data: cell_map_data, zoneSide: zoneSide });
                    }
                });

                perspectivePoints.push(leftMostPoint, rightMostPoint);

                // If we have encroachment data, render it on the canvas
                if (encroachmentBboxes.length > 0 && zoneBboxes.length > 0) {
                    // fabric canvas group that will hold all the active cells (i.e. cells with encroachment). This group is then masked
                    // off using the clip path generated from the encroachment bbox to ensure no pixels are drawn outside the encroachment bbox
                    let activeCellGroup = new fabric.Group([], { selectable: false });
                    let cellMapDataPresent = false;
                    zoneBboxes.forEach((zoneBbox, index) => {
                        // For each zone bbox (up to three per frame - left, four-foot, and right), generate label color and bboxes that will
                        // be used for the rest of the process

                        // const labelColor = getColorFromPercentage(zoneBbox.percentage);
                        const newZoneBbox = generateZoomableImageBbox(zoneBbox.bbox, ratioChangeForZoom, "rgba(255, 255, 255, 0.09)", 0.09, 1);
                        // const newZoneBbox = generateZoomableImageBbox(zoneBbox.bbox, ratioChangeForZoom, "rgba(255, 255, 255, 0.09)", 0.09);
                        fabricCanvasObjects.current.push(newZoneBbox);

                        // Note: label code commented out for now as there are too many percentage labels if there are more than a few zones. Leaving this
                        // code here in case we want to re-enable it in the future, or we figure out how to display the percentages effectively.

                        // If we have a percentage in the response, generate a label for the percentage, and color it based on the percentage
                        // if (zoneBbox.percentage) {
                        //     const label = `${Math.ceil(zoneBbox.percentage)}%`;
                        //     const side = zoneBbox.zoneSide;
                        //     const textBox = generateZoomableImageBboxLabel(zoneBbox.bbox, ratioChangeForZoom, label, labelColor, newHeight, 1, side);
                        //     fabricCanvasObjects.current.push(textBox);
                        // }

                        if (zoneBbox.cell_map_data && zoneBbox.cell_map_data.num_rows && zoneBbox.cell_map_data.num_cols) {
                            cellMapDataPresent = true;
                            // Generate the grid for the zone bbox and the cells with encroachment. First get the grid dimensions and the cell states.
                            // The cell states are an array of 1s and 0s, where 1 indicates an active cell (i.e. encroachment) and 0 indicates an inactive cell
                            const gridRows = zoneBbox.cell_map_data.num_rows;
                            const gridCols = zoneBbox.cell_map_data.num_cols;
                            const cellStates = zoneBbox.cell_map_data.cell_map;

                            // Render the grid
                            generateZoneGrids(gridRows, gridCols, newZoneBbox, fabricCanvas, zoneBbox.percentage, activeCellGroup, cellStates);
                        } else {
                            // If we don't have cell map data, just add the zone bbox to the canvas
                            fabricCanvasObjects.current.push(newZoneBbox);
                        }
                    });

                    encroachmentBboxes.forEach((encroachmentBbox) => {
                        if (!cellMapDataPresent) {
                            // If we don't have cell map data, generate the bbox and color it based on the percentage
                            const color = getColorFromPercentage(encroachmentBbox.percentage);
                            const newEncroachmentBbox = generateZoomableImageBbox(encroachmentBbox.bbox, ratioChangeForZoom, color);
                            fabricCanvasObjects.current.push(newEncroachmentBbox);
                            // fabricCanvas.add(newEncroachmentBbox);
                        }
                    });

                    fabricCanvasObjects.current.push(activeCellGroup);
                } else if (!loadingImage && selectedMarker && ((overlayBoxes && overlayBoxes.length) || (overlays && overlays.length))) {
                    overlayBoxes.forEach((bbox, index) => {
                        let bboxColor = "#CCCCCC";
                        if (selectedMarker.class !== "Troughing" || index !== 0) {
                            bboxColor = setBboxColor(selectedMarker);
                            let bboxLabel = null;

                            if (selectedMarker.class === "Thermal Hotspot") {
                                const temp = _.get(selectedMarker, "temperature", 0);
                                const degreeValue = degreesToUserPreference(temp, tempUnitPreference);
                                const unit = getTempUnit(tempUnitPreference);
                                bboxLabel = `${degreeValue}${unit}`;
                            } else {
                                bboxLabel = tag;
                            }

                            if (bboxLabel) {
                                const label = generateZoomableImageBboxLabel(bbox, ratioChangeForZoom, bboxLabel, bboxColor, newHeight, overlayWidth, 1);
                                fabricCanvasObjects.current.push(label);
                            }
                        }
                        const box = generateZoomableImageBbox(bbox, ratioChangeForZoom, bboxColor);
                        fabricCanvasObjects.current.push(box);
                    });

                    if (!(frame || videoKey) || (selectedMarker.video_key === videoKey && selectedMarker.frame === frame)) {
                        overlays.forEach((overlay) => {
                            if (overlay.type === "polygon") {
                                fabricCanvasObjects.current.push(
                                    new fabric.Polygon(
                                        overlay.points.map((p) => ({
                                            x: p.x * ratioChangeForZoom,
                                            y: p.y * ratioChangeForZoom,
                                        })),
                                        {
                                            ...overlay.options,
                                            selectable: false,
                                        },
                                    ),
                                );
                            } else if (overlay.type === "rect") {
                                fabricCanvasObjects.current.push(
                                    new fabric.Rect({
                                        left: overlay.left * ratioChangeForZoom,
                                        top: overlay.top * ratioChangeForZoom,
                                        width: overlay.width * ratioChangeForZoom,
                                        height: overlay.height * ratioChangeForZoom,
                                        ...overlay.options,
                                        selectable: false,
                                    }),
                                );
                            } else if (overlay.type === "circle") {
                                const [x, y] = overlay.center;

                                fabricCanvasObjects.current.push(
                                    new fabric.Circle({
                                        left: x * ratioChangeForZoom,
                                        top: y * ratioChangeForZoom,
                                        radius: overlay.radius * ratioChangeForZoom,
                                        ...overlay.options,
                                        selectable: false,
                                    }),
                                );
                            } else if (overlay.type === "textbox") {
                                const text = new fabric.Text(overlay.text, {
                                    fontSize: 12,
                                    fontFamily: "Helvetica",
                                    fontWeight: 500,
                                    fill: "rgba(255, 255, 255, 1)",
                                    ...(overlay.textOptions ?? {}),
                                    originX: "center",
                                    originY: "center",
                                });

                                const padding = overlay.padding ?? 0;

                                const bg = new fabric.Rect({
                                    width: text.width + padding * 2,
                                    height: text.height + padding * 2,
                                    fill: "rgba(0, 0, 0, 1)",
                                    ...(overlay.rectOptions ?? {}),
                                    originX: "center",
                                    originY: "center",
                                });

                                fabricCanvasObjects.current.push(
                                    new fabric.Group([bg, text], {
                                        left: 0,
                                        top: 0,
                                        ...(overlay.position ?? {}),
                                        selectable: false,
                                    }),
                                );
                            }
                        });
                    }
                }

                fabricCanvas.add(...fabricCanvasObjects.current);
                fabricCanvas.renderAll();
            }
        }, [
            observationDetections,
            frame,
            videoKey,
            ratioChangeForZoom,
            markers,
            newHeight,
            overlayWidth,
            fabricCanvas,
            showOverlayContent,
            loadingImage,
            tag,
            overlayBoxes,
            overlays,
            formattedImageSource,
            tempUnitPreference,
        ]);

        useEffect(() => {
            if (fabricCanvasRef) {
                const dpi = window.devicePixelRatio || 1;

                const canvasElement = fabricCanvasRef;
                const width = canvasElement.clientWidth;
                const height = canvasElement.clientHeight;

                canvasElement.width = width * dpi;
                canvasElement.height = height * dpi;

                const canvas = new fabric.Canvas(canvasElement, {
                    defaultCursor: "default",
                    uniformScaling: false,
                    selection: false,
                    containerClass: "ZoomableImageCanvasContainer",
                });

                setFabricCanvas(canvas);
            }
        }, [fabricCanvasRef]);

        const limitCanvasPosition = useCallback((canvas) => {
            const canvasViewPort = canvas.viewportTransform;

            const bottomEndPoint = canvas.height * (canvasViewPort[0] - 1);
            if (canvasViewPort[5] >= 0 || -bottomEndPoint > canvasViewPort[5]) {
                canvasViewPort[5] = canvasViewPort[5] >= 0 ? 0 : -bottomEndPoint;
            }

            const rightEndPoint = canvas.width * (canvasViewPort[0] - 1);
            if (canvasViewPort[4] >= 0 || -rightEndPoint > canvasViewPort[4]) {
                canvasViewPort[4] = canvasViewPort[4] >= 0 ? 0 : -rightEndPoint;
            }
            canvas.viewportTransform = canvasViewPort;
        }, []);

        const onMove = useCallback(
            (opt, canvas) => {
                if (canvas.isDragging) {
                    const delta = new fabric.Point(opt.e.movementX, opt.e.movementY);
                    canvas.relativePan(delta);
                    limitCanvasPosition(canvas);
                }
            },
            [limitCanvasPosition],
        );

        const onScroll = useCallback(
            (opt) => {
                if (disabled) return;

                let zoom = fabricCanvas.getZoom();
                if (opt) {
                    const delta = opt.e.deltaY;
                    zoom *= 0.999 ** delta;
                    zoom = Math.max(Math.min(20, zoom), 1);
                    fabricCanvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
                    opt.e.preventDefault();
                    opt.e.stopPropagation();
                }
                limitCanvasPosition(fabricCanvas);
                if (updateZoom) {
                    updateZoom(zoom);
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [fabricCanvas, limitCanvasPosition],
        );

        useEffect(() => {
            if (fabricCanvas) {
                fabricCanvas.on("mouse:down", function (_) {
                    this.isDragging = true;
                });

                fabricCanvas.on("mouse:move", function (opt) {
                    onMove(opt, this);
                });

                fabricCanvas.on("mouse:up", function (_) {
                    this.setViewportTransform(this.viewportTransform);
                    this.isDragging = false;
                });

                fabricCanvas.on("mouse:wheel", function (opt) {
                    onScroll(opt);
                });

                return () => {
                    fabricCanvas.__eventListeners["mouse:move"] = [];
                    fabricCanvas.__eventListeners["mouse:down"] = [];
                    fabricCanvas.__eventListeners["mouse:up"] = [];
                    fabricCanvas.__eventListeners["mouse:wheel"] = [];
                };
            }
        }, [fabricCanvas, onMove, onScroll]);

        const imageFilters = useMemo(() => {
            const imageFilters = [];
            if (!_.isNil(filters.brightness)) {
                imageFilters.push(new fabric.Image.filters.Brightness({ brightness: filters.brightness }));
            }
            if (!_.isNil(filters.contrast)) {
                imageFilters.push(new fabric.Image.filters.Contrast({ contrast: filters.contrast }));
            }
            return imageFilters;
        }, [filters]);

        useEffect(() => {
            if (canvasImageRef.current && (canvasImageRef.current.naturalWidth || canvasImageRef.current.width)) {
                canvasImageRef.current.filters = imageFilters;
                canvasImageRef.current.applyFilters();
                fabricCanvas.renderAll();
            }
        }, [imageFilters, fabricCanvas]);

        useEffect(() => {
            const callback = (imageSrc) => {
                setFormattedImageSource(imageSrc);
                setImageCacheTimestamp(Date.now());
            };

            if (strideImageData) {
                setLoadingImage(true);
                asyncLoadImageWithRange(strideImageData.url, strideImageData.range, strideImageData.csrfToken).then(callback);
            } else if (imgSrc && currentDashboard) {
                setLoadingImage(true);
                getRawApiImage(imgSrc, currentDashboard.access_token, callback);
            } else if (imgSrc) {
                setFormattedImageSource(imgSrc);
            }
        }, [currentDashboard, imgSrc, strideImageData]);

        useEffect(() => {
            if (fabricCanvas) {
                fabric.Image.fromURL(
                    formattedImageSource,
                    function (img) {
                        if (canvasImageRef.current) {
                            fabricCanvas.remove(canvasImageRef.current);
                        }

                        const widthRatioChange = dimensions.width / img.width;
                        const heightRatioChange = dimensions.height / img.height;

                        let ratioChange;
                        if (img.width * heightRatioChange > dimensions.width) {
                            ratioChange = widthRatioChange;
                            setRatioChangeForZoom(widthRatioChange);
                        } else {
                            ratioChange = heightRatioChange;
                            setRatioChangeForZoom(heightRatioChange);
                        }

                        const newWidth = img.width * ratioChange;
                        const newHeight = img.height * ratioChange;

                        setNewHeight(newHeight);
                        setOverlayWidth(newWidth);

                        fabricCanvas.setWidth(newWidth);
                        fabricCanvas.setHeight(newHeight);

                        const imageData = {
                            left: 0,
                            top: 0,
                            controls: {},
                            hasBorders: false,
                            selectable: false,
                            scaleY: ratioChange,
                            scaleX: ratioChange,
                        };

                        const oImg = img.set(imageData);
                        fabricCanvas.add(oImg);
                        oImg.sendToBack();
                        fabricCanvas.renderAll();

                        canvasImageRef.current = oImg;
                        setLoadingImage(false);

                        if (!isPictureInPicture) return;

                        dispatch(setZoomableImageDimensions({ width: img.width, height: img.height }));
                    },
                    { crossOrigin: "anonymous" },
                );
            }
        }, [dimensions.height, dimensions.width, dispatch, fabricCanvas, formattedImageSource, imageCacheTimestamp, isPictureInPicture]);

        useEffect(() => {
            if (fabricCanvas && canvasImageRef.current) {
                const currentZoom = fabricCanvas.getZoom();
                if (currentZoom !== zoom) {
                    let x = fabricCanvas.width / 2;
                    let y = fabricCanvas.height / 2;

                    if (overlayBoxes && overlayBoxes[0] && ratioChangeForZoom) {
                        const [left, top, width, height] = overlayBoxes[0];
                        let leftOffset = 0;
                        let topOffset = 0;
                        if (left * ratioChangeForZoom < fabricCanvas.width / 2) {
                            leftOffset = width * ratioChangeForZoom;
                        }
                        if (top * ratioChangeForZoom < fabricCanvas.height / 2) {
                            topOffset = height * ratioChangeForZoom;
                        }
                        x = left * ratioChangeForZoom + (width * ratioChangeForZoom - leftOffset);
                        y = top * ratioChangeForZoom + (height * ratioChangeForZoom - topOffset);
                    }

                    fabricCanvas.zoomToPoint(new fabric.Point(x, y), zoom);
                    limitCanvasPosition(fabricCanvas);
                    fabricCanvas.renderAll();
                }
            }
        }, [fabricCanvas, zoom, ratioChangeForZoom, overlayBoxes, limitCanvasPosition]);

        return (
            <>
                {loadingImage && withSpinner && (
                    <div
                        className="SpinnerContainer"
                        style={{ position: "absolute", zIndex: 2000 }}>
                        <OBCSpinner
                            size={80}
                            speed={3}
                            colorScheme={"mono"}
                        />
                    </div>
                )}
                <Measure
                    bounds
                    onResize={(contentRect) => {
                        setDimensions(contentRect.bounds);
                    }}>
                    {({ measureRef }) => {
                        return (
                            <div
                                className="ZoomableImage"
                                ref={measureRef}>
                                <canvas
                                    ref={(c) => setFabricCanvasRef(c)}
                                    className={`ZoomFabricCanvas  ${rounded && "rounded-8"}`}
                                />
                            </div>
                        );
                    }}
                </Measure>
            </>
        );
    },
);

export default ZoomableImage;
